import React from 'react';
import { usePermissions } from '@/shared/hooks/usePermissions';
import { cn } from '@/shared/lib/utils';

export interface WithPermissionProps {
  /**
   * Single permission required to show the component
   * Example: PERMISSIONS.VEHICLE_CREATE, PERMISSIONS.TRIP_UPDATE
   */
  permission?: string;
  
  /**
   * Multiple permissions - checked with AND/OR logic
   * Example: [PERMISSIONS.VEHICLE_VIEW, PERMISSIONS.VEHICLE_UPDATE]
   */
  permissions?: string[];
  
  /**
   * When using multiple permissions:
   * - true: User must have ALL permissions (AND logic)
   * - false: User must have ANY permission (OR logic)
   * Default: false (OR logic)
   */
  requireAll?: boolean;
  
  /**
   * What to render when user lacks permission
   * - 'hide': Render nothing (default for buttons)
   * - 'disable': Render disabled element (for buttons)
   * - 'text': Render as plain text (for links)
   */
  fallback?: 'hide' | 'disable' | 'text';
  
  /**
   * The component(s) to conditionally render
   */
  children: React.ReactNode;
}

/**
 * WithPermission - Wrapper component for permission-based UI visibility
 * 
 * Features:
 * - Supports single or multiple permissions
 * - AND/OR logic for multiple permissions
 * - Configurable fallback behavior (hide, disable, or show as text)
 * - Works with buttons, links, and any React component
 * 
 * @example
 * // Hide button when no permission
 * <WithPermission permission={PERMISSIONS.VEHICLE_CREATE}>
 *   <Button onClick={handleCreate}>New Vehicle</Button>
 * </WithPermission>
 * 
 * @example
 * // Show link as text when no permission
 * <WithPermission permission={PERMISSIONS.VEHICLE_UPDATE} fallback="text">
 *   <Link to="/vehicles/1/edit">Edit Vehicle</Link>
 * </WithPermission>
 * 
 * @example
 * // Multiple permissions with OR logic (user needs ANY)
 * <WithPermission permissions={[PERMISSIONS.TRIP_VIEW, PERMISSIONS.TRIP_UPDATE]}>
 *   <Link to="/trips">Trips</Link>
 * </WithPermission>
 * 
 * @example
 * // Multiple permissions with AND logic (user needs ALL)
 * <WithPermission permissions={[PERMISSIONS.TRIP_CREATE, PERMISSIONS.TRIP_VIEW]} requireAll={true}>
 *   <Button>Create Trip</Button>
 * </WithPermission>
 */
export const WithPermission: React.FC<WithPermissionProps> = ({
  permission,
  permissions,
  requireAll = false,
  fallback = 'hide',
  children,
}) => {
  const { hasPermission, hasPermissions } = usePermissions();

  // Check permissions
  let hasAccess = false;

  if (permission) {
    // Single permission check
    hasAccess = hasPermission(permission);
  } else if (permissions && permissions.length > 0) {
    // Multiple permissions check with AND/OR logic
    hasAccess = hasPermissions(permissions, requireAll);
  } else {
    // No permission specified - show by default
    hasAccess = true;
  }

  // If user has access, render children normally
  if (hasAccess) {
    return <>{children}</>;
  }

  // Handle different fallback strategies when user lacks permission
  switch (fallback) {
    case 'hide':
      // Don't render anything
      return null;

    case 'disable': {
      // Render disabled version of the component
      // Clone the child element and add disabled prop
      if (React.isValidElement(children)) {
        return React.cloneElement(children as React.ReactElement<{disabled?: boolean; className?: string}>, {
          disabled: true,
          className: cn(
            (children as React.ReactElement<{className?: string}>).props.className,
            'opacity-50 cursor-not-allowed'
          ),
        });
      }
      return null;
    }

    case 'text': {
      // Render as plain text (for links)
      // Extract text content from children
      const extractText = (node: React.ReactNode): string => {
        if (typeof node === 'string') return node;
        if (typeof node === 'number') return String(node);
        if (React.isValidElement(node)) {
          return extractText(node.props.children);
        }
        if (Array.isArray(node)) {
          return node.map(extractText).join('');
        }
        return '';
      };

      const textContent = extractText(children);
      return (
        <span className="text-text cursor-default">
          {textContent}
        </span>
      );
    }

    default:
      return null;
  }
};
